import React from 'react'
import './ScheduleSettings.scss'
import {Formik, Form} from "formik";
import * as Yup from "yup";
import {Checkbox, DateTime, Number, Time} from "../../../components/InputComponents";
import { useTranslation } from "react-i18next";
import {selectIsPlaylistQuickPublishPopupOpen, selectIsTemplateQuickPublishPopupOpen} from "../../../state/creativesSlice";
import { useSelector } from 'react-redux'
import {InformationTooltip} from "../../../components/InformationTooltip/InformationTooltip";
import {HelpText} from "../../../components/HelpText/HelpText";

const initialValues = {
    duration: '',
    alwaysPlay: true,
    overrule: false,
    displayFromToggle: false,
    displayFrom: '',
    displayFromAnnually: '',
    displayUntilToggle: false,
    displayUntil: '',
    displayUntilAnnually: '',
    repeatYearly: false,

    displayOnToggle: true,
    startFrom: '',
    endAt: '',

    monday: false,
    mondayStartFrom: '',
    mondayEndAt: '',
    tuesday: false,
    tuesdayStartFrom: '',
    tuesdayEndAt: '',
    wednesday: false,
    wednesdayStartFrom: '',
    wednesdayEndAt: '',
    thursday: false,
    thursdayStartFrom: '',
    thursdayEndAt: '',
    friday: false,
    fridayStartFrom: '',
    fridayEndAt: '',
    saturday: false,
    saturdayStartFrom: '',
    saturdayEndAt: '',
    sunday: false,
    sundayStartFrom: '',
    sundayEndAt: '',
};

export default function ScheduleSettings({onPublish}) {
    const { t } = useTranslation();
    const isTemplateQuickPublishPopupOpen = useSelector(selectIsTemplateQuickPublishPopupOpen)
    const isPlaylistQuickPublishPopupOpen = useSelector(selectIsPlaylistQuickPublishPopupOpen)

    return (
        <div id="popup-container" style={{minHeight: '100%'}}>
            <h3>
                {t('quick_publish.scheduling_settings.title')}
                <InformationTooltip>
                    {
                        {
                            'template': t('quick_publish.scheduling_settings.template_info'),
                            'playlist': t('quick_publish.scheduling_settings.playlist_info'),
                        }[isTemplateQuickPublishPopupOpen ? 'template' : 'playlist']
                    }
                </InformationTooltip>
            </h3>

            <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={Yup.object({
                    duration: isTemplateQuickPublishPopupOpen ? Yup.number().integer().required() : Yup.number().nullable(),
                    alwaysPlay: Yup.boolean().required(),
                    overrule: isPlaylistQuickPublishPopupOpen ? Yup.boolean().required() : Yup.boolean().nullable(),
                    repeatYearly: Yup.boolean(),
                    displayFromToggle: Yup.boolean(),
                    displayFrom: Yup.date().when(['displayFromToggle', 'repeatYearly'], {
                        is: (displayFromToggle, repeatYearly) => displayFromToggle && !repeatYearly,
                        then: () => {return Yup.date().nullable()},
                        otherwise: () => {return Yup.date().nullable()},
                    }),
                    displayFromAnnually: Yup.date().when(['displayFromToggle', 'repeatYearly'], {
                        is: (displayFromToggle, repeatYearly) => displayFromToggle && repeatYearly,
                        then: () => {return Yup.date().nullable()},
                        otherwise: () => {return Yup.date().nullable()},
                    }),

                    displayUntilToggle: Yup.boolean(),
                    displayUntil: Yup.date().when(['displayUntilToggle', 'repeatYearly'], {
                        is: (displayUntilToggle, repeatYearly) => displayUntilToggle && !repeatYearly,
                        then: () => {return Yup.date().nullable()},
                        otherwise: () => {return Yup.date().nullable()},
                    }),
                    displayUntilAnnually: Yup.date().when(['displayUntilToggle', 'repeatYearly'], {
                        is: (displayUntilToggle, repeatYearly) => displayUntilToggle && repeatYearly,
                        then: () => {return Yup.date().nullable()},
                        otherwise: () => {return Yup.date().nullable()},
                    }),

                    displayOnToggle: Yup.boolean(),
                    startFrom: Yup.string().nullable(),
                    endAt: Yup.string().nullable(),

                    monday: Yup.boolean(),
                    mondayStartFrom: Yup.string().nullable(),
                    mondayEndAt: Yup.string().nullable(),
                    tuesday: Yup.boolean(),
                    tuesdayStartFrom: Yup.string().nullable(),
                    tuesdayEndAt: Yup.string().nullable(),
                    wednesday: Yup.boolean(),
                    wednesdayStartFrom: Yup.string().nullable(),
                    wednesdayEndAt: Yup.string().nullable(),
                    thursday: Yup.boolean(),
                    thursdayStartFrom: Yup.string().nullable(),
                    thursdayEndAt: Yup.string().nullable(),
                    friday: Yup.boolean(),
                    fridayStartFrom: Yup.string().nullable(),
                    fridayEndAt: Yup.string().nullable(),
                    saturday: Yup.boolean(),
                    saturdayStartFrom: Yup.string().nullable(),
                    saturdayEndAt: Yup.string().nullable(),
                    sunday: Yup.boolean(),
                    sundayStartFrom: Yup.string().nullable(),
                    sundayEndAt: Yup.string().nullable(),
                })}
                onSubmit={async (values, {setSubmitting}) => {
                    await onPublish(values);
                    setSubmitting(false);
                }}
            >
                {formik => (
                    <Form>
                        <fieldset>
                            {isTemplateQuickPublishPopupOpen &&
                                <Number
                                    label={t('quick_publish.scheduling_settings.duration')}
                                    name="duration"
                                    required={true}
                                    helpText={t('quick_publish.scheduling_settings.duration_help_text')}
                                />
                            }

                            {isPlaylistQuickPublishPopupOpen && <>
                                <Checkbox
                                    label={t('quick_publish.scheduling_settings.over_rule')}
                                    name="overrule"
                                />
                                <HelpText>
                                    {t('quick_publish.scheduling_settings.over_rule_help_text')}
                                </HelpText>
                            </>}

                            <Checkbox
                                label={t('quick_publish.scheduling_settings.always_play')}
                                name="alwaysPlay"
                            />

                            {!formik.values.alwaysPlay &&
                                <>
                                    <hr/>

                                    <div className="row" style={{marginBottom: '8px'}}>
                                        <div className="col-md-6">
                                            <Checkbox
                                                label={t('quick_publish.scheduling_settings.display_from')}
                                                name="displayFromToggle"
                                                onChange={(e) => {
                                                    formik.setFieldValue('displayFromToggle', e.target.checked)
                                                    if (!e.target.checked) {
                                                        formik.setFieldValue('repeatYearly', false)
                                                    }
                                                }}
                                            />
                                            {formik.values.displayFromToggle &&
                                                <div id="display-from-datetime-container">
                                                    {formik.values.repeatYearly
                                                        ?
                                                        <DateTime
                                                            name="displayFromAnnually"
                                                            placeholderText={t('input_component.date_picker_placeholder')}
                                                            shouldCloseOnSelect={false}
                                                            showTimeInput
                                                            dateFormat="MM/dd HH:mm"
                                                        />
                                                        :
                                                        <DateTime
                                                            name="displayFrom"
                                                            placeholderText={t('input_component.date_picker_placeholder')}
                                                            shouldCloseOnSelect={false}
                                                            showTimeInput
                                                            dateFormat="yyyy/MM/dd HH:mm"
                                                        />
                                                    }
                                                </div>
                                            }
                                        </div>
                                        <div className="col-md-6">
                                            <Checkbox
                                                label={t('quick_publish.scheduling_settings.display_until')}
                                                name="displayUntilToggle"
                                                onChange={(e) => {
                                                    formik.setFieldValue('displayUntilToggle', e.target.checked)
                                                    if (!e.target.checked) {
                                                        formik.setFieldValue('repeatYearly', false)
                                                    }
                                                }}
                                            />
                                            {formik.values.displayUntilToggle &&
                                                <div id="display-until-datetime-container">
                                                    {formik.values.repeatYearly
                                                        ?
                                                        <DateTime
                                                            name="displayUntilAnnually"
                                                            placeholderText={t('input_component.date_picker_placeholder')}
                                                            shouldCloseOnSelect={false}
                                                            showTimeInput
                                                            dateFormat="MM/dd HH:mm"
                                                        />
                                                        :
                                                        <DateTime
                                                            name="displayUntil"
                                                            placeholderText={t('input_component.date_picker_placeholder')}
                                                            shouldCloseOnSelect={false}
                                                            showTimeInput
                                                            dateFormat="yyyy/MM/dd HH:mm"
                                                        />
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <Checkbox
                                        disabled={!(formik.values.displayFromToggle && formik.values.displayUntilToggle)}
                                        label={t('quick_publish.scheduling_settings.repeat_yearly')}
                                        name="repeatYearly"
                                    />

                                    {!(formik.values.displayFromToggle && formik.values.displayUntilToggle) &&
                                        <HelpText disabled={true}>
                                            {t('quick_publish.scheduling_settings.repeat_yearly_help_text')}
                                        </HelpText>
                                    }

                                    <hr/>

                                    <Checkbox
                                        label={t('quick_publish.scheduling_settings.display_on_toggle')}
                                        name="displayOnToggle"
                                    />
                                    {formik.values.displayOnToggle
                                        ?
                                        (
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <Time
                                                        label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                        name="startFrom"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <Time
                                                        label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                        name="endAt"
                                                    />
                                                </div>
                                            </div>
                                        )
                                        :
                                        (
                                            <div id="play-on-days-container">
                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_monday')}
                                                        name="monday"
                                                    />
                                                </div>
                                                {formik.values.monday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="mondayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="mondayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_tuesday')}
                                                        name="tuesday"
                                                    />
                                                </div>
                                                {formik.values.tuesday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="tuesdayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="tuesdayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_wednesday')}
                                                        name="wednesday"
                                                    />
                                                </div>
                                                {formik.values.wednesday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="wednesdayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="wednesdayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_thursday')}
                                                        name="thursday"
                                                    />
                                                </div>
                                                {formik.values.thursday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="thursdayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="thursdayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_friday')}
                                                        name="friday"
                                                    />
                                                </div>
                                                {formik.values.friday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="fridayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="fridayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_saturday')}
                                                        name="saturday"
                                                    />
                                                </div>
                                                {formik.values.saturday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="saturdayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="saturdayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }

                                                <div className="play-on-day">
                                                    <Checkbox
                                                        label={t('quick_publish.scheduling_settings.display_on_sunday')}
                                                        name="sunday"
                                                    />
                                                </div>
                                                {formik.values.sunday &&
                                                    <div className="row play-on-day-time-container">
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.start_playing_at')}
                                                                name="sundayStartFrom"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <Time
                                                                label={t('quick_publish.scheduling_settings.stop_playing_on')}
                                                                name="sundayEndAt"
                                                            />
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        )
                                    }
                                </>
                            }
                            <button type="submit" className="btn btn-dpm publish-button">{t('generic.publish')}</button>
                        </fieldset>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

